import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { AlertCircle, MailCheck, Lock } from "lucide-react";

const PERSONALITY = ["FUN", "DILIGENT", "APPROACHABLE", "RESPONSIBLE", "CURIOUS"];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const [snap, setSnap] = useState(null);
  const notice = location.state || null;

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    api.get("/public/snapshot").then(r => setSnap(r.data)).catch(() => {});
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[480px_1fr] bg-brand-paper" data-testid="login-page">
      {/* Left: Auth Panel */}
      <div className="flex flex-col justify-between p-8 lg:p-12 border-r border-brand-line bg-white relative">
        <div className="flex items-start gap-3" data-testid="brand-logo">
          <img src="/logo.webp" alt="goSTOPS" className="w-12 h-12 rounded-2xl object-cover shadow-sm" />
          <div>
            <div className="font-display font-black text-lg leading-none tracking-tight logo-wordmark">
              <span>g</span><span>o</span><span>S</span><span>T</span><span>O</span><span>P</span><span>S</span>
            </div>
            <div className="font-mono-tech text-[9px] uppercase tracking-[0.3em] text-zinc-500 mt-1.5">Business development portal</div>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-sm">
          <div className="font-script text-2xl text-brand-pink mb-2 -rotate-1">say hi 👋</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-brand-ink leading-[0.95]">
            Run the
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">expansion</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-brand-yellow -z-0 -rotate-1"></span>
            </span>
            <span className="text-brand-pink">.</span>
          </h1>
          <p className="mt-6 text-base text-zinc-700 leading-relaxed">
            The internal command center for goSTOPS BD crew. Approve fresh stops, watch the deals, and act on intelligence — all in one place.
          </p>

          <button
            onClick={handleGoogle}
            data-testid="google-login-btn"
            className="mt-10 group flex items-center justify-between w-full bg-brand-ink text-white px-5 py-4 rounded-2xl hover:bg-brand-yellow hover:text-brand-ink transition-colors duration-200"
          >
            <span className="flex items-center gap-3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M22.5 12.27c0-.79-.07-1.55-.2-2.27H12v4.3h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.22-4.74 3.22-8.09z" fill="#FFCC00"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.05-3.71 1.05-2.85 0-5.27-1.92-6.13-4.5H2.18v2.84A11 11 0 0 0 12 23z" fill="#fff"/>
                <path d="M5.87 14.14a6.62 6.62 0 0 1 0-4.27V7.03H2.18a11 11 0 0 0 0 9.94l3.69-2.83z" fill="#FFCC00"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.03l3.69 2.84C6.73 7.3 9.15 5.38 12 5.38z" fill="#fff"/>
              </svg>
              <span className="font-display font-bold tracking-tight">Continue with Google</span>
            </span>
            <span className="font-mono-tech text-xs opacity-60 group-hover:opacity-100">→</span>
          </button>

          <div className="mt-4 flex items-start gap-2 text-[11px] leading-relaxed text-zinc-500" data-testid="access-policy-note">
            <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-zinc-400" />
            <span>
              Only <span className="font-mono-tech font-semibold text-brand-ink">@gostops.com</span> email addresses can access this system.
              New users must request access from an admin after signing in.
            </span>
          </div>

          {notice?.pending && (
            <div className="mt-5 flex items-start gap-3 bg-brand-yellow/15 border border-brand-yellow rounded-lg p-3" data-testid="pending-banner">
              <MailCheck className="w-4 h-4 mt-0.5 text-brand-ink flex-shrink-0" />
              <div className="text-xs leading-relaxed text-brand-ink">
                <div className="font-display font-bold">Access request sent</div>
                <div className="mt-1 text-zinc-700">
                  {notice.message || "Your access request has been sent to the admin for review. You'll be notified once approved."}
                </div>
                {notice.email && <div className="mt-1 font-mono-tech text-[10px] text-zinc-500 uppercase tracking-wider">{notice.email}</div>}
              </div>
            </div>
          )}
          {notice?.rejected && (
            <div className="mt-5 flex items-start gap-3 bg-brand-pink/10 border border-brand-pink rounded-lg p-3" data-testid="rejected-banner">
              <AlertCircle className="w-4 h-4 mt-0.5 text-brand-pink flex-shrink-0" />
              <div className="text-xs leading-relaxed text-brand-ink">
                <div className="font-display font-bold">Sign-in blocked</div>
                <div className="mt-1 text-zinc-700">
                  {notice.message || "This email is not permitted. Only @gostops.com addresses can access the BD portal."}
                </div>
              </div>
            </div>
          )}

          <div className="brand-underline w-32 mt-8 mb-3"></div>
        </div>

        <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex justify-between">
          <span>v1.3</span>
          <span>BD-OPS · IN</span>
        </div>
      </div>

      {/* Right: Hero */}
      <div className="hidden lg:flex relative overflow-hidden bg-brand-ink">
        <div className="absolute inset-0 dotted-grid opacity-40" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_30%,rgba(255,204,0,0.22)_0%,transparent_55%),radial-gradient(circle_at_15%_75%,rgba(255,105,180,0.18)_0%,transparent_50%)]" />

        {/* floating colored circles */}
        <div className="absolute top-12 right-16 w-24 h-24 rounded-full bg-brand-pink/30 blur-2xl" />
        <div className="absolute bottom-32 left-12 w-32 h-32 rounded-full bg-brand-teal/20 blur-2xl" />

        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-16 w-full">
          <div className="flex items-center gap-4 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-400">
            <span className="w-2 h-2 bg-brand-yellow rounded-full animate-pulse"></span>
            <span>LIVE · {snap?.latest_month || "MAR 2026"} · {snap?.operational_properties ?? 31} stops · {snap?.states_count ?? 11} states</span>
          </div>

          <div>
            <div className="font-script text-3xl text-brand-yellow mb-3 -rotate-1">live social.</div>
            <h2 className="font-display font-black text-white text-5xl xl:text-7xl leading-[0.92] tracking-tight">
              GEN Z STAYS HERE.
              <br />
              <span className="text-brand-yellow">YOU RUN IT HERE.</span>
            </h2>
            <p className="mt-8 max-w-lg text-zinc-300 text-base leading-relaxed">
              From fresh stop approvals to AI-scored partnership intelligence — anchored to live MIS. Every property, every state, every signal in one operational view.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {PERSONALITY.map((p, i) => (
                <span
                  key={p}
                  className="font-display font-bold text-xs tracking-tight px-3 py-1 rounded-full"
                  style={{ background: ["#FFCC00", "#FF69B4", "#20B2AA", "#FFA500", "#9370DB"][i], color: "#09090B" }}
                  data-testid={`pill-${p}`}
                >
                  {p}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 max-w-2xl border-t border-zinc-800 pt-6">
            {[
              [snap?.operational_properties ?? "—", "Operational stops", "#FFCC00"],
              [snap ? `${snap.total_capacity_beds}` : "—", "Total bed capacity", "#FF69B4"],
              [snap ? `${snap.avg_occupancy_pct}%` : "—", `Avg occupancy ${snap?.latest_month || ""}`, "#20B2AA"],
            ].map(([k, v, c]) => (
              <div key={v} className="border-l-2 pl-3" style={{ borderColor: c }}>
                <div className="font-display font-black text-3xl text-white tracking-tight">{k}</div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
